import React from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { setMode } from '../../redux/theme';
import { RootState } from 'typesafe-actions';
import { FlexRow, BodyText, SvgIcon } from '@make-software/cspr-ui';
import { Trans, useTranslation } from 'react-i18next';

export enum ThemeModeType {
  light = 'light',
  dark = 'dark',
}

export const ThemeModeIconPath = {
  [ThemeModeType.light]: 'assets/icons/ic-light-mode.svg',
  [ThemeModeType.dark]: 'assets/icons/ic-dark-mode.svg',
};

const StyledSvgIcon = styled(SvgIcon)<{ isActive: boolean }>(
  ({ theme, isActive }) => ({
    svg: {
      path: {
        fill: theme.styleguideColors.contentTertiary,
      },
    },
    ':hover svg': {
      path: {
        fill: theme.styleguideColors.contentOnFill,
      },
    },
    ...(isActive && {
      path: {
        fill: theme.styleguideColors.contentOnFill,
      },
    }),
  })
);

export interface ThemeModeToggleProps {
  withTitle?: boolean;
}

/**
 * @deprecated Remove after CSPR.click integration
 */
export const ThemeModeToggle = ({
  withTitle = false,
}: ThemeModeToggleProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    theme: { mode },
    featureSpotlights: {
      theme: { visited, expired },
    },
  } = useSelector((state: RootState) => state);

  const themeButtonLabel = {
    [ThemeModeType.light]: t('light mode'),
    [ThemeModeType.dark]: t('dark mode'),
  };

  const nextMode =
    mode === ThemeModeType.light ? ThemeModeType.dark : ThemeModeType.light;

  const handleClick = () => {
    dispatch(setMode(nextMode));

    window.gtag('event', 'click', {
      event_category: 'dark_mode',
      event_label: `${nextMode} mode is selected`,
    });
  };

  return (
    <FlexRow itemsSpacing={8} align="center" onClick={handleClick}>
      <StyledSvgIcon
        src={ThemeModeIconPath[nextMode]}
        isActive={!(visited && expired)}
        className="dark-theme-spotlight-target"
      />
      {withTitle ? (
        <BodyText size={3} variation="lightGray">
          <Trans t={t}>
            Switch to {{ nextMode: themeButtonLabel[nextMode] }}
          </Trans>
        </BodyText>
      ) : null}
    </FlexRow>
  );
};
